<script>
  import { onDestroy, onMount, tick } from "svelte";
  import dayjs from "dayjs";
  import Loading from "../../../components/pages/Loading.svelte";
  import {
    getOperationStartDateFromFixedProperty,
    getFixedProperty
  } from "../../../utils/getData.js";
  import { usePageContext } from "../../../components/pages/pageContext.js";
  import {
    getDispatchingViewConfiguration,
    saveDispatchingViewConfiguration
  } from "../../../utils/configurationStorage/views/DispatchingViewConfigurationStorage.js";
  import GeneralButton from "../../../components/button/GeneralButton.svelte";
  import { translate } from "../../../i18n/i18next.js";
  import { navigateI18n } from "../../../i18n/navigate.js";
  import { useProject, useProjectQuery } from "../../../hooks/useProject.js";
  import { getTimeStringSec } from "../../../utils/time.js";
  import { useTimezone } from "../../../hooks/useTimezone.js";
  import DispatchingViewControl from "./DispatchingViewControl.svelte";
  import { getResourceNameOrCode } from "./utils.js";
  import DispatchingViewSideButton from "./DispatchingViewSideButton.svelte";
  import { applySearchParam } from "../../../utils/searchParams/applySearchParam.js";
  import TableHeaderCell from "../../../components/table/TableHeaderCell.svelte";
  import TableCell from "../../../components/table/TableCell.svelte";

  import { getNotificationsContext } from "svelte-notifications"
  import ResultEdit from "../../../components/ganttchart/ResultEdit.svelte";
  import { formatDateTime } from "../../../utils/dateFormatter";
  import { getFetchErrorHandlerContext } from "../../../hooks/useFetchErrorHandlerContext";
  import { writable } from "svelte/store";

  const { addNotification } = getNotificationsContext();


  export let projectId
  export let code
  export let user
  export let resources
  export let resourceGroups
  export let styles
  export let setSelectedStyleIndex
  export let columns
  export let operations
  export let uploadTimestamp
  export let dateList
  export let globalProjectData
  export let postTransaction
  export let reloadTransactions
  export let setPrefetchedResourceObjectIds
  export let isInputAllowed

  $: resourceObjectID = code
  $: dayStartTime = globalProjectData?.Project_DayStartTime

  const {getOffset} = useTimezone();

  const {setHeaderProps} = usePageContext();
  $: setHeaderProps({uploadTime: uploadTimestamp});
  const project = useProject(projectId)

  $: iStyle = 0;

  let showResultEdit = false;
  let operationToEditResult = null;

  const {handleNonBlockingFetchErrors} = getFetchErrorHandlerContext()

  const enableResultEdit = writable(true);

  const projectQuery = useProjectQuery(projectId, {
    onSuccess: data => ((settings) => {
      enableResultEdit.set(settings.isDispatchingViewResultEditEnabled === undefined ? true : settings.isDispatchingViewResultEditEnabled);
    })(data.settings),
  })
  // please keep the line below to force the above query to run
  // as I am yet to know how to do so on svelte-query declaratively...
  console.log($projectQuery)


  const editResultOnClick = async (operation) => {
    showResultEdit = true;
    operationToEditResult = operation;
  }

  const editResultOnSubmit = async(newStartDateTimeString, newEndDateTimeString, newStatus, newQty, newProgress, newObtainTime) => {
    const operationCode = getFixedProperty(operationToEditResult, "OperationCode");
    const transaction = {
      [operationCode]: {
        Work_Status: newStatus,
        Work_ResultStartTime: newStartDateTimeString,
        Work_ResultEndTime: newEndDateTimeString,
        Work_ResultQty: newQty,
        Work_ResultProgress: newProgress,
        Work_ResultObtainTime: newObtainTime
      },
    };
    try {
      const ok = await postTransaction(transaction, resourceObjectID, 1500)
      if (ok) {
        addNotification({
          text: translate("generic.saved"),
          position: "top-right",
          removeAfter: 4000,
          type: "success",
        });
        showResultEdit = false;
        await refresh();
      }
    } catch (e) {
      if (e instanceof FetchError) {
        handleNonBlockingFetchErrors(e.error);
      }
    }
  }

  let usingDatePageMode = false;
  let itemPerPage = 10
  let currentPageIndex = 0
  let displayUnitTermColumn = false
  let displayCompletedTasks = false;

  $: allDisplayableOperations = operations?.filter(operation => {
    // determine which operations should be on the list of displayable operations
    // if "displayCompletedTasks" setting is on, display all.
    if (displayCompletedTasks) return true
    // else if the operation is not completed, display it
    if (getFixedProperty(operation, "Status") !== "B") return true
    // else if the operation has "_removedAt" property, which is injected by onClickStartEndButton(), compare the timestamp with now.
    return operation._removeAt && operation._removeAt > Date.now()
  }) ?? []
  $: totalPageNumber = usingDatePageMode ? dateList.length : Math.floor(allDisplayableOperations.length / itemPerPage) + 1
  $: displayedOperations = filterOperationsToBeDisplayed(allDisplayableOperations, usingDatePageMode, itemPerPage, dateList, currentPageIndex)
  $: rowSpans = ((disOpe) => {
    // Get rowSpans
    let rowSpans = [];
    let upperUnitTerm = null;
    let upperUnitTermIndex = -1;
    disOpe.forEach((operation, index) => {
      let unitTerm = operation.ASGDispatchingViewRowObject_UnitTermStartTime;
      if (!upperUnitTerm || unitTerm !== upperUnitTerm) {
        upperUnitTerm = unitTerm;
        upperUnitTermIndex = index;
        rowSpans.push(1);
        return;
      }
      // if the unit term is the same as the previous one, increase the row span of the previous row
      // Skip this td
      rowSpans.push(-1);
      rowSpans[upperUnitTermIndex] += 1;
    });
    return rowSpans;
  })(displayedOperations);

  let isFirstVisit = false;

  /**
   * @type {{
   *   objectId: string,
   *   code: string,
   *   name: string,
   *   displayOrder: number,
   * } | null}
   */
  let currentResource = null

  /**
   * @type {{
   *   objectId: string,
   *   code: string,
   *   name: string,
   *   displayOrder: number,
   *   groupMembers: string[],
   * } | null}
   */
  let currentResourceGroup = null

  function filterOperationsToBeDisplayed(allOperations, usingDatePageMode, itemPerPage, dateList, currentPageIndex) {
    return allOperations.filter((operation, index) => {
      if (usingDatePageMode) return getOperationStartDateFromFixedProperty(operation, dayStartTime) === dateList[currentPageIndex]
      return currentPageIndex * itemPerPage <= index && index <= (currentPageIndex + 1) * itemPerPage - 1
    })
  }

  function determineColumnsToBeDisplayed(columns, displayUnitTermColumn) {
    const keysSet = new Set(columns.map((column) => column.key))
    return columns.filter(column => {
      if (column.key === "Work_ResultStartTime" && keysSet.has("UseInst_StartTime")) return false
      if (column.key === "Work_ResultEndTime" && keysSet.has("UseInst_EndTime")) return false
      if (column.key === "ASGDispatchingViewRowObject_UnitTermStartTime" && !displayUnitTermColumn) return false
      return true
    })
  }
  $: displayedColumns = determineColumnsToBeDisplayed(columns, displayUnitTermColumn)

  function styleSelectOnChange() {
    // sync the style name on url query parameter
    applySearchParam("style", styles[iStyle])

    setSelectedStyleIndex(iStyle)
  }

  async function pageModeOnChange() {
    currentPageIndex = 0
    await refresh();
  }

  async function refresh(isAutoRefresh = false) {
    try {
      currentResource = resources.find(r => (r.objectId === resourceObjectID))
      if (!isAutoRefresh && currentResource)
        setHeaderProps({title: currentResource.name || currentResource.code});

      // determine in which resource group we will cycle when clicking the side buttons
      // 1. if the current resource does not belong to any resource group, set null.
      // 2. if the current resource only belongs to a resource group, set it.
      // 3. if resource group selection is saved in the localStorage, set the resource group if the current resource belongs to it.
      // 4. otherwise set the resource group whose number of members is the smallest among the resource groups the resource belongs to.
      const resourceGroupCandidates = resourceGroups.filter(rg => rg.groupMembers.find(member => member.code === currentResource.code))
      if (resourceGroupCandidates.length === 0) {
        currentResourceGroup = null
      } else if (resourceGroupCandidates.length === 1) {
        currentResourceGroup = resourceGroupCandidates[0]
      } else {
        const savedResourceGroupCode = getDispatchingViewConfiguration(user.id, projectId).selectedResourceGroupCode
        const savedResourceGroup = savedResourceGroupCode ? resourceGroupCandidates.find(rg => rg.code === savedResourceGroupCode) : null
        if (savedResourceGroup) {
          currentResourceGroup = savedResourceGroup
        } else {
          let mostSpecificResourceGroup = null
          let numMembers = Infinity
          for (const rg of resourceGroupCandidates) {
            if (rg.groupMembers.length < numMembers) {
              numMembers = rg.groupMembers.length
              mostSpecificResourceGroup = rg
            }
          }
          currentResourceGroup = mostSpecificResourceGroup
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  function saveViewDate() {
    if (!currentResource) return
    const savedSettings = getDispatchingViewConfiguration(user.id, projectId);
    const viewDates = Object.fromEntries((savedSettings?.viewDates ?? []).map(obj => [obj.resourceObjectID, obj.viewDate]))
    viewDates[currentResource.objectId] = new Date().toISOString()
    saveDispatchingViewConfiguration({viewDates: Object.entries(viewDates).map(entry => ({resourceObjectID: Number(entry[0]), viewDate: entry[1]}))}, user.id, projectId);
  }

  let intervalHandle = null

  onMount(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    isFirstVisit = urlParams.has('firstVisit');

    // Do not save the view date if it is the first visit
    // because "first visit" implies that the user did not come to this resource voluntarily.
    if (!isFirstVisit) {
      saveViewDate();
    }

    // load the saved settings from localStorage
    await loadSavedSettings(user.id, projectId)

    // sync the specified style name from url query parameters
    await loadSearchParams()
    setSelectedStyleIndex(iStyle)
    applySearchParam("style", styles[iStyle])

    await refresh();
  });

  onDestroy(() => {
    if (intervalHandle) clearInterval(intervalHandle)
  })

  async function loadSavedSettings(userId, projectId) {
    // load settings from localStorage and apply them
    if (userId && projectId) {
      const savedSettings = getDispatchingViewConfiguration(userId, projectId);
      // apply style settings
      //   before applying it, check if the saved settings value is valid
      if (savedSettings.styleName) {
        const index = styles.findIndex((s) => s === savedSettings.styleName);
        if (index !== -1) {
          iStyle = index;
        }
      }
      // apply itemsPerPage
      if (savedSettings.itemPerPage) {
        itemPerPage = savedSettings.itemPerPage;
      }
      if (savedSettings.displayCompletedTasks) {
        displayCompletedTasks = savedSettings.displayCompletedTasks;
      }
      if (savedSettings.displayUnitTermColumn) {
        displayUnitTermColumn = savedSettings.displayUnitTermColumn;
      }
      // apply usingDatePageMode
      if (savedSettings.usingDatePageMode) {
        usingDatePageMode = savedSettings.usingDatePageMode;
        return pageModeOnChange();
      }
    }
  }

  async function loadSearchParams() {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("style")) {
      const specifiedStyleName = searchParams.get("style");
      // check if the specified style name is valid
      const index = styles.findIndex((s) => s === specifiedStyleName);
      if (index !== -1) iStyle = index;
    }
  }

  function getNeighborResource(currentResourceGroup, currentResource, direction) {
    if (!currentResourceGroup || !currentResource) return {}
    const currentIndex = currentResourceGroup.groupMembers.findIndex(member => member.code === currentResource.code);
    if (direction === "back") {
      return currentResourceGroup.groupMembers[currentIndex - 1 < 0 ? currentResourceGroup.groupMembers.length - 1 : currentIndex - 1]
    } else {
      return currentResourceGroup.groupMembers[currentIndex + 1 >= currentResourceGroup.groupMembers.length ? 0 : currentIndex + 1]
    }
  }

  async function moveToResource(destinationResourceObjectId) {
    navigateI18n(
      `/projects/${projectId}/DispatchingView/${destinationResourceObjectId}`,
      {replace: true},
    );
    resourceObjectID = destinationResourceObjectId
    await tick()
    await reloadTransactions()
    await refresh()

    // save the viewDate for the resource in the localStorage
    saveViewDate();
  }

  /**
   *
   * @param {"back"|"next"} direction
   */
  async function moveInResourceGroup(direction) {
    if (!currentResourceGroup || !currentResource) return

    const neighborResource = getNeighborResource(currentResourceGroup, currentResource, direction)

    await moveToResource(neighborResource.objectId)
    await reloadTransactions()
  }

  // transactions: start button click
  async function onStartEndButtonClick(event, operation, transactionType) {
    // if project data is not ready yet, reject submission
    if (!$project?.timezone) return
    const currentDateTimeString = getTimeStringSec(new Date(), $project.timezone, $getOffset)

    // prepare the payload
    const operationCode = getFixedProperty(operation, "OperationCode");
    const updated = {
      Work_Status: transactionType === "start" ? "T" : "B",
      Work_ResultStartTime:
        transactionType === "start" ? currentDateTimeString : operation["Work_ResultStartTime"] ? dayjs(operation["Work_ResultStartTime"]).format("YYYY/MM/DD HH:mm:ss") : undefined,
      Work_ResultEndTime:
        transactionType === "end" ? currentDateTimeString : undefined,
      Work_ResultMainRes: "",
      Work_ResultQty: null,
      Work_ResultProgress: null,
      Work_ResultObtainTime: null
    }
    const transaction = {
      [operationCode]: updated,
    }

    await postTransaction(transaction, currentResource.objectId, 1500);
  }

  $: neighborResources = [getNeighborResource(currentResourceGroup, currentResource, "back"), getNeighborResource(currentResourceGroup, currentResource, "next")];
  $: setPrefetchedResourceObjectIds(neighborResources.map(res => res.objectId))
</script>

<div class="content-root">
  <DispatchingViewControl
    bind:iStyle
    {styles}
    {styleSelectOnChange}
    bind:displayCompletedTasks
    bind:displayUnitTermColumn
    bind:itemPerPage={itemPerPage}
    {pageModeOnChange}
    bind:usingDatePageMode
    {totalPageNumber}
    {dateList}
    projectId={projectId}
    userId={user.id}
    bind:currentIndex={currentPageIndex}
    {dayStartTime}
    {currentResource}
    bind:currentResourceGroup
    {resources}
    {resourceGroups}
    {isFirstVisit}
    onMoveToResource={moveToResource}
    {enableResultEdit}
  />

  <!-- Main Table -->
  <div class="tables-container">
    {#if currentResourceGroup && currentResourceGroup.groupMembers.length > 1}
      <DispatchingViewSideButton
        onClick={() => moveInResourceGroup("back")}
        disabled={currentResource.code === getNeighborResource(currentResourceGroup, currentResource, "back").code}
        position="left"
      >
        {getResourceNameOrCode(getNeighborResource(currentResourceGroup, currentResource, "back"))}
        <img src="/assets/icons/icon_page_left.svg" alt="icon left" style="transform: rotate(90deg)"/>
      </DispatchingViewSideButton>
    {/if}
    {#if displayedOperations.length > 0}
      <div class="operations-table-container">
        <table>
          <thead>
          <tr id="head">
            {#each displayedColumns as column}
              <th>{column.displayedString}</th>
            {/each}
            {#if $enableResultEdit && isInputAllowed}
              <TableHeaderCell stickToRight={true}>{translate("generic.editResultShort")}</TableHeaderCell>
            {/if}
          </tr>
          </thead>
          {#each displayedOperations as operation, i}
            <tr>
              {#each displayedColumns as column}
                {#if column.key === "UseInst_StartTime" || column.key === "Work_ResultStartTime"}
                  <!--if useinststart/endtime exist, show button in that column, or in the resultstart/endtime columns-->
                  <td>
                    <div class="result-container">
                      <div>
                        <!-- Scheduled start date, get it using fixed property since this columns may not be included in the table -->
                        {formatDateTime(getFixedProperty(operation, "StartTime"))}
                      </div>
                      <div
                        style="height: 1px; background-color: #B5BDC3; width: 90%; margin: 0 auto;"
                      />
                      {#if operation["Work_ResultStartTime"] !== ""}
                        <div style="color:#249E8E;" class="result-time-text">
                          {formatDateTime(operation["Work_ResultStartTime"])}
                        </div>
                      {:else if operation["Work_ResultStartTime"] === "" && getFixedProperty(operation, "Status") === "B"}
                        <div style="color:#249E8E;">
                          {translate("asprova.terms.operationStatuses.completed")}
                        </div>
                      {:else}
                        <GeneralButton
                          buttonDisabled={!isInputAllowed}
                          buttonFunction={(e) => onStartEndButtonClick(e, operation, "start")}
                          buttonValue={`startEndButton$${i}`}
                          buttonText={translate("generic.startShort")}
                          buttonExtraStyle="width: 96%"
                        />
                      {/if}
                    </div>
                  </td>
                {:else if column.key === "UseInst_EndTime" || column.key === "Work_ResultEndTime"}
                  <td>
                    {#key operation[column.key]}
                      <div class="result-container">
                        <div>
                          <!-- Scheduled end date, get it using fixed property since this columns may not be included in the table -->
                          {formatDateTime(getFixedProperty(operation, "EndTime"))}
                        </div>
                        <div
                          style="height: 1px; background-color: #B5BDC3; width: 90%; margin: 0 auto;"
                        />
                        {#if operation["Work_ResultEndTime"] !== ""}
                          <div style="color:#249E8E;" class="result-time-text">
                            {formatDateTime(operation["Work_ResultEndTime"])}
                          </div>
                        {:else if operation["Work_ResultEndTime"] === "" && getFixedProperty(operation, "Status") === "B"}
                          <div style="color:#249E8E;">
                            {translate("asprova.terms.operationStatuses.completed")}
                          </div>
                        {:else}
                          <GeneralButton
                            buttonDisabled={!isInputAllowed || operation["Work_ResultStartTime"] === ""}
                            buttonFunction={(e) => onStartEndButtonClick(e, operation, "end")}
                            buttonValue={`startEndButton$${i}`}
                            buttonText={translate("generic.finishShort")}
                            buttonExtraStyle="width: 96%"
                          />
                        {/if}
                      </div>
                    {/key}
                  </td>
                {:else}
                  {#if column.key !== "ASGDispatchingViewRowObject_UnitTermStartTime"}
                    <td>{operation[column.key]}</td>
                  {:else if rowSpans[i] != -1}
                    <td rowspan={rowSpans[i]}>{operation[column.key]}</td>
                  {/if}
                {/if}
              {/each}
              {#if $enableResultEdit && isInputAllowed}
                <TableCell stickToRight={true} isResultEditButton={true} resultEditButtonOnClicked={() => editResultOnClick(operation)}/>
              {/if}
            </tr>
          {/each}
        </table>
      </div>
    {:else if operations.length > 0 && displayedOperations.length === 0}
      <div style="flex: 1">
        <Loading height="100%" message={translate("frontend:views.dispatchingView.noUnfinishedOperations")} mark="!"/>
      </div>
    {:else}
      <div style="flex: 1">
        <Loading height="100%" message={translate("frontend:views.dispatchingView.noOperation")} mark="!"/>
      </div>
    {/if}
    {#if currentResourceGroup && currentResourceGroup.groupMembers.length > 1}
      <DispatchingViewSideButton
        onClick={() => moveInResourceGroup("next")}
        disabled={currentResource.code === getNeighborResource(currentResourceGroup, currentResource, "next").code}
        position="right"
      >
        <img src="/assets/icons/icon_page_right.svg" alt="icon right" style="transform: rotate(-90deg)"/>
        {getResourceNameOrCode(getNeighborResource(currentResourceGroup, currentResource, "next"))}
      </DispatchingViewSideButton>
    {/if}
  </div>
</div>

{#if showResultEdit && operationToEditResult}
  <ResultEdit
    operationCode={getFixedProperty(operationToEditResult, "OperationCode")}
    prevStartDateTime={getFixedProperty(operationToEditResult, "ResultStartTime")}
    prevEndDateTime={getFixedProperty(operationToEditResult, "ResultEndTime")}
    prevStatus={getFixedProperty(operationToEditResult, "Status")}
    prevQty={getFixedProperty(operationToEditResult, "ResultQty")}
    itemQty={getFixedProperty(operationToEditResult, "OutputQty")}
    prevProgress={getFixedProperty(operationToEditResult, "ResultProgress")}
    prevObtainTime={getFixedProperty(operationToEditResult, "ResultObtainTime")}
    scheduledStartDateObject={new Date(getFixedProperty(operationToEditResult, "StartTime"))}
    scheduledEndDateObject={new Date(getFixedProperty(operationToEditResult, "EndTime"))}
    onSubmit={editResultOnSubmit}
    onClose={()=>showResultEdit = false}
  />
{/if}

<style>
  .content-root {
    height: 100%;
    max-height: 100%;
    display: flex;
    flex-direction: column;
  }

  .tables-container {
    display: flex;
    flex-direction: row;
    gap: .3rem;
    align-items: stretch;
    height: 100%;
    min-height: 0;
  }

  table {
    width: 100%;
    text-align: center;
    border-collapse: collapse;
    table-layout: fixed;
  }

  table tr:nth-child(even) {
    background-color: #f2f2f2;
  }

  table tr:nth-child(odd) {
    background-color: white;
  }

  th {
    background-color: #28385d;
    position: sticky;
    top: 0;
    z-index: 20;
    color: white;
    font-size: 1rem;
    padding: 8px;
  }

  td {
    border: 1px solid #d3d3d3;
    border-collapse: collapse;
    padding: 0 5px;
    line-height: 30px;
    white-space: pre-wrap;
    overflow-wrap: break-word;
  }

  /* td[rowspan] {
    vertical-align: top;
  } */

  .result-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .result-container > div {
    max-width: 100%;
  }

  .operations-table-container {
    flex: 1 1 auto;
    overflow-y: auto;
    /* height: 0; */
  }

  /**
    This is to hide one of the duplicate elements caused by manually replacing button with a div with a text.
    See onStartEndButtonClick().
  */
  :global(div.result-time-text + div.result-time-text) {
    display: none;
  }
</style>
